import React, { useState, useEffect, useRef } from "react";
import LogoImg from "../../assets/images/logonew.png";
import NotificationIcon from "../../assets/icons/notification";
import { getInitials, timeAgo } from "../../Utils/getUserUtitls";
import MenuIcon from "../../assets/icons/menu";
import { getUser, hasPermission, isAdmin, isHR } from "../../Utils/auth";
import api from "../../setupMiddleware";
import { useLocation } from "react-router-dom";

const Navbar = ({ toggleSidebar, user }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [empData, setEmpData] = useState([]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);

  const notifRef = useRef(null);
  const currentUser = getUser();

  // click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/notifications") {
      // mark all notifications as read
      setTimeout(() => setNotificationCount(0), 1000);
    }
  }, [location]);

  // Filter suggestions based on search value
  const filteredSuggestions =
    empData.length > 0 &&
    empData.filter((item) =>
      item?.employee_name.toLowerCase().includes(searchValue?.toLowerCase())
    );

  const getAllUser = async () => {
    try {
      api.get("/api/employee-list").then((res) => {
        if (res && res.status === 200) {
          setEmpData(res.data?.employees);
        }
      });
    } catch (err) {
      console.error(err);
    }
  };
  const getAllTeamUser = async () => {
    try {
      api.get("/api/team-members").then((res) => {
        if (res && res.status === 200) {
          const resData = res?.data?.data;
          const teamData = resData?.map((em) => ({
            ...em,
            employee_name: [em?.first_name, em?.middle_name, em?.last_name]
              .filter(Boolean)
              .join(" "),
          }));
          setEmpData(teamData);
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getNotification = async () => {
    api
      .get(`/api/notifications/${currentUser?.id}`)
      .then((res) => {
        if (res && res.status === 200) {
          const notiArray = res.data?.data;
          const newNoti = notiArray?.filter((not) => !not.is_read);
          setNotifications(newNoti);
          setNotificationCount(newNoti?.length);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const setReadNotification = async () => {
    try {
      api
        .post(`/api/notifications/mark-all-read`, { user_id: currentUser?.id })
        .then((res) => {
          if (res && res.status === 200) {
            setNotificationCount(0);
          }
        });
    } catch (err) {
      console.error(err);
    }
  };

  const hasViewPermission = hasPermission("view_employees");
  const hasTeamViewPermission = hasPermission("view_team_employees");

  useEffect(() => {
    if (isAdmin() || isHR()) {
      getAllUser();
    }
    if (isAdmin() || hasTeamViewPermission) {
      getAllTeamUser();
    }
    getNotification();
  }, []);

  useEffect(() => {
    if (showNotifications && notificationCount > 0) {
      setReadNotification();
    }
  }, [showNotifications]);
  return (
    <>
      <div className="w-full h-[88px] px-6 flex justify-between items-center bg-white border-b border-[#142b70]/10">
        {/* Left: Logo + Hamburger for mobile */}
        <div className="flex items-center gap-4">
          <button
            className="md:hidden p-2 border rounded-lg"
            onClick={toggleSidebar}
          >
            <MenuIcon color="#de2b26" />
          </button>
          <img
            src={LogoImg}
            className="w-full max-w-[140px] h-[50px]"
            alt="logo"
          />
          {(isAdmin() || hasViewPermission) && (
            <div className="relative">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search..."
                  className={`
                transition-all duration-300 ease-in-out
                pl-4 pr-2 py-2 ml-10
                rounded-full border border-[#de2b26]/40 shadow-sm
                text-sm placeholder:text-[#142b70]/60 text-[#142b70]
                w-80 opacity-100
                focus:outline-none focus:ring-0
              `}
                  style={{ overflow: "hidden" }}
                  autoFocus={showSearch}
                />
              </div>

              {/* Suggestions dropdown */}
              {searchValue && filteredSuggestions.length > 0 && (
                <ul className="absolute top-12 left-0 w-64 bg-white border border-[#de2b26]/20 rounded-lg shadow-lg z-50">
                  {filteredSuggestions.map((item, idx) => (
                    <li
                      key={idx}
                      className="px-4 py-2 text-sm text-[#142b70] hover:bg-[#de2b26]/10 cursor-pointer"
                      onClick={() => {
                        window.location = `/view-employee/${item.id}`;
                      }}
                    >
                      {item.employee_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-5">
          {/* 🔔 Notification with Badge */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setShowNotifications((prev) => !prev)}
              className="relative p-2"
            >
              <NotificationIcon
                className="w-6 h-6"
                color="#142b70"
                strokeWidth="1.4"
              />
              {notificationCount > 0 && (
                <span className="absolute -top-[3px] -right-[2px] bg-red-500 text-white text-[11px] font-bold p-[3px] text-center rounded-full w-[22px] h-[22px]">
                  {notificationCount > 9 ? "9+" : notificationCount}
                </span>
              )}
            </button>

            {/* Notification Panel */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-96 bg-white border border-[#de2b26]/20 rounded-lg shadow-lg z-50">
                <div className="px-4 py-2 text-sm font-medium text-[#142b70] border-b">
                  Notifications
                </div>
                <div className="flex flex-col gap-0 max-h-60 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className="flex items-start bg-[#c30f0a]/5 p-4 border-b"
                      >
                        {/* content */}
                        <div className="flex flex-col">
                          <span className="text-[12px] text-[#c30f0a]">
                            {n.type === "leave"
                              ? "Leave Approved"
                              : n.type === "leave_application"
                              ? "Leave Applied"
                              : n.type === "user_creation"
                              ? "User Created"
                              : ""}
                          </span>
                          <span className="text-[12px] text-[#142b70]">
                            {n.message}
                          </span>
                          <span className="text-xs text-[#142b70]/50">
                            {timeAgo(n.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <li className="px-4 py-2 text-sm text-[#142b70]/60">
                      No new notifications
                    </li>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* <Divider color="#142b70" /> */}
          <div className="flex items-center gap-2">
            {user?.employee_image ? (
              <img
                src={user?.employee_image}
                alt="profile pic"
                className="w-10 h-10 rounded-full border-[1px] border-[#de2b26] object-cover"
              />
            ) : (
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#de2b26] text-[#ffffff]">
                {getInitials(user?.first_name + " " + user?.last_name)}
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p className="text-xs text-[#142b70]">
                {user?.first_name + " " + user?.last_name}
              </p>
              <p className="text-[10px] text-[#142b70]/80">
                {user?.role?.name}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
